/**
 * シーンのクロップ / リフレーム (映像の一部だけを使う) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-J。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/crop.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/crop.py (AI 提案の正規化・合成フレーム・要約)
 *
 * ## 意味
 * - `x` / `y` / `w` / `h`: **元素材のフレームに対する %** (左上が原点・0.1 刻み)。
 *   `w` / `h` は 10〜100 で、矩形は素材の内側にクランプする (`normalizeCrop`)。
 *   省略 = クロップなし。
 * - `fit`: 切り出した矩形を出力にどう収めるか。`contain` (既定) = 全体を収める (余白は黒)、
 *   `cover` = 出力を埋める (はみ出しは切る)。
 *
 * **出力の比率 (project.output) は変えない**。横→縦の流用で比率そのものを変えるのは
 * 媒体規定準拠計画 (docs/features/video-distribution-media-spec-plan.md) の範囲。
 *
 * ## 表示
 * クロップ層は `overflow: hidden` のラッパ (出力サイズ) で、その中の映像 (`objectFit: contain`)
 * に `translate(x%, y%) scale(s)` を掛けて「切り出した矩形が出力の contain / cover 矩形に
 * 一致する」ようにする (cropTransform)。translate の % は出力サイズ基準
 * (transform は右から適用されるので拡大の影響を受けない。schema/motion.ts と同じ作法)。
 *
 * 層の位置は **motion の内側・look の内側**で、シーンのオーバーレイ (rect が出力座標) は
 * クロップ層の**外**に置く (クロップの影響を受けない)。
 */
import { z } from 'zod';

/** 切り出した矩形の収め方。contain = 全体を収める (余白は黒) / cover = 出力を埋める。 */
export const CROP_FITS = ['contain', 'cover'] as const;
export type CropFit = (typeof CROP_FITS)[number];

/** fit を省略したときの解釈。 */
export const DEFAULT_CROP_FIT: CropFit = 'contain';

/** w / h (元素材に対する %) の範囲。10% 未満の切り出しは画質が持たないので認めない。 */
export const CROP_SIZE_MIN = 10;
export const CROP_SIZE_MAX = 100;
/** x / y / w / h の刻み (%)。 */
export const CROP_STEP_PERCENT = 0.1;

/**
 * 保存形。**範囲をスキーマで縛らない** (壊れた JSON でプロジェクトごと開けなくならないよう、
 * 範囲のクランプと丸めは normalizeCrop が使うたびに行う。DuckingSchema と同じ方針)。
 */
export const CropSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  fit: z.enum(CROP_FITS).optional(),
});
export type Crop = z.infer<typeof CropSchema>;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 0.1 刻みへの half-up 丸め (backend の crop.py と同じ式)。 */
function roundStep(value: number): number {
  return Math.floor(value * 10 + 0.5) / 10;
}

/**
 * 小数 4 桁への half-up 丸め (backend の crop.py の `_round4` と同じ規則)。
 * **-0 は 0 に正規化する** (JS は -0 を返すが Python は 0.0 を返すため、
 * 3 実装の同値テストが符号で割れる)。
 */
function round4(value: number): number {
  const rounded = Math.floor(value * 10000 + 0.5) / 10000;
  return rounded === 0 ? 0 : rounded;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/** 省略時の既定 (contain) を埋めた fit。 */
export function resolveCropFit(crop: Crop | null | undefined): CropFit {
  return crop?.fit === 'cover' ? 'cover' : DEFAULT_CROP_FIT;
}

/**
 * 外部入力 (AI の提案・古い保存データ・UI の数値) を保存形へ正規化する。
 * **保存値 = 実効値**にするため、範囲へクランプして 0.1 刻みに丸めてから
 * 「全面 + contain」(= 何も切り出していない) を undefined に倒す。
 * 全面 + cover は「出力を埋める」という意味のある指定なので残す。
 */
export function normalizeCrop(value: unknown): Crop | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const rawX = finiteNumber(raw.x);
  const rawY = finiteNumber(raw.y);
  const rawW = finiteNumber(raw.w);
  const rawH = finiteNumber(raw.h);
  if (rawX === null || rawY === null || rawW === null || rawH === null) return undefined;
  const w = roundStep(clamp(rawW, CROP_SIZE_MIN, CROP_SIZE_MAX));
  const h = roundStep(clamp(rawH, CROP_SIZE_MIN, CROP_SIZE_MAX));
  const x = roundStep(clamp(rawX, 0, CROP_SIZE_MAX - w));
  const y = roundStep(clamp(rawY, 0, CROP_SIZE_MAX - h));
  const fit = raw.fit === 'cover' ? 'cover' : DEFAULT_CROP_FIT;
  if (fit === DEFAULT_CROP_FIT && x === 0 && y === 0 && w === CROP_SIZE_MAX && h === CROP_SIZE_MAX) {
    return undefined;
  }
  return fit === 'cover' ? { x, y, w, h, fit } : { x, y, w, h };
}

export interface CropTransform {
  /** 映像に掛ける拡大率。 */
  scale: number;
  /** 出力幅に対する % (正 = 右へ)。 */
  translateXPercent: number;
  /** 出力高さに対する % (正 = 下へ)。 */
  translateYPercent: number;
  /** 切り出した矩形が画面上で占める領域 (出力 px)。contain なら余白、cover なら画面いっぱい。 */
  boxPx: { x: number; y: number; w: number; h: number };
}

/**
 * クロップ層の変形を求める (3 実装で同値)。
 *
 * 前提: 映像は出力サイズのボックスに `objectFit: contain` で描かれている
 * (素材は `min(outW/srcW, outH/srcH)` 倍で中央に置かれる)。その状態から、
 * 切り出した矩形が出力の contain / cover 矩形に一致するよう拡大・平行移動する。
 *
 * 素材寸法・出力寸法のどれかが 0 以下なら null (= クロップ層を足さない)。
 */
export function cropTransform(
  crop: Crop,
  fit: CropFit,
  srcWidth: number,
  srcHeight: number,
  outWidth: number,
  outHeight: number,
): CropTransform | null {
  if (!(srcWidth > 0) || !(srcHeight > 0) || !(outWidth > 0) || !(outHeight > 0)) return null;
  const base = Math.min(outWidth / srcWidth, outHeight / srcHeight);
  const displayWidth = srcWidth * base;
  const displayHeight = srcHeight * base;
  const regionWidth = (displayWidth * crop.w) / 100;
  const regionHeight = (displayHeight * crop.h) / 100;
  if (!(regionWidth > 0) || !(regionHeight > 0)) return null;
  const scale =
    fit === 'cover'
      ? Math.max(outWidth / regionWidth, outHeight / regionHeight)
      : Math.min(outWidth / regionWidth, outHeight / regionHeight);
  // 切り出した矩形の中心が、映像の中心からどれだけずれているか (表示 px)
  const centerX = ((crop.x + crop.w / 2) / 100 - 0.5) * displayWidth;
  const centerY = ((crop.y + crop.h / 2) / 100 - 0.5) * displayHeight;
  const boxWidth = regionWidth * scale;
  const boxHeight = regionHeight * scale;
  return {
    scale: round4(scale),
    translateXPercent: round4((-centerX * scale * 100) / outWidth),
    translateYPercent: round4((-centerY * scale * 100) / outHeight),
    boxPx: {
      x: round4((outWidth - boxWidth) / 2),
      y: round4((outHeight - boxHeight) / 2),
      w: round4(boxWidth),
      h: round4(boxHeight),
    },
  };
}

/** CSS の transform 文字列 (motion と同じく translate → scale の順)。 */
export function cropTransformCss(transform: CropTransform): string {
  return `translate(${transform.translateXPercent}%, ${transform.translateYPercent}%) scale(${transform.scale})`;
}

/** UI / 要約 / README に出す 1 行 (backend の describe_crop と同じ文言)。 */
export function cropLabel(crop: Crop | null | undefined): string {
  if (!crop) return 'なし';
  const fit = resolveCropFit(crop) === 'cover' ? '埋める' : '収める';
  return `x${crop.x} y${crop.y} w${crop.w} h${crop.h}% (${fit})`;
}
